import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  bridgeService,
  type Device,
  type Workstation,
} from "../services/bridgeService";
import {
  workstationPreferenceService,
  type PreferredPrinterPreference,
  type PreferredPrinterType,
} from "../services/workstationPreference.service";

interface PrinterCatalogEntry {
  device: Device;
  workstation: Workstation;
}

interface PrinterCatalog {
  entries: PrinterCatalogEntry[];
  loadedWorkstationIds: string[];
  failedWorkstationCount: number;
}

async function loadPrinterCatalog(): Promise<PrinterCatalog> {
  const workstations = await bridgeService.getAllWorkstations();
  const results = await Promise.all(
    workstations.map(async (workstation) => {
      try {
        const response = await bridgeService.getDevices(workstation.id);
        return { workstation, devices: response.data.data };
      } catch {
        return { workstation, devices: undefined };
      }
    })
  );
  const loaded = results.filter((result) => !!result.devices);
  return {
    entries: loaded.flatMap((result) =>
      (result.devices ?? []).map((device) => ({
        device,
        workstation: result.workstation,
      }))
    ),
    loadedWorkstationIds: loaded.map((result) => result.workstation.id),
    failedWorkstationCount: results.length - loaded.length,
  };
}

function buildPreference(
  entry: PrinterCatalogEntry,
  type: PreferredPrinterType
): PreferredPrinterPreference {
  if (entry.device.deviceType !== type) {
    throw new Error("The selected printer has the wrong device type");
  }
  return {
    deviceId: entry.device.id,
    deviceName: entry.device.name,
    deviceType: type,
    workstationId: entry.workstation.id,
    workstationName: entry.workstation.name,
  };
}

function resolvePreference(
  type: PreferredPrinterType,
  deviceId: string | null,
  catalog: PrinterCatalog
): PreferredPrinterPreference | null {
  if (!deviceId) return null;
  const entry = catalog.entries.find(({ device }) => device.id === deviceId);
  if (entry) return buildPreference(entry, type);

  const existing = workstationPreferenceService.getPreferredPrinter(type);
  const workstationFailedToLoad =
    existing?.deviceId === deviceId &&
    !catalog.loadedWorkstationIds.includes(existing.workstationId);
  if (existing && workstationFailedToLoad) return existing;
  throw new Error("The selected printer is no longer available");
}

function persistPreference(
  type: PreferredPrinterType,
  preference: PreferredPrinterPreference | null
): void {
  if (preference) {
    workstationPreferenceService.setPreferredPrinter(preference);
    return;
  }
  workstationPreferenceService.clearPreferredPrinter(type);
}

function persistPreferences(
  cassette: PreferredPrinterPreference | null,
  slide: PreferredPrinterPreference | null
): void {
  const originalCassette =
    workstationPreferenceService.getPreferredPrinter("cassette_printer");
  const originalSlide =
    workstationPreferenceService.getPreferredPrinter("slide_printer");
  try {
    persistPreference("cassette_printer", cassette);
    persistPreference("slide_printer", slide);
  } catch (error) {
    try {
      persistPreference("cassette_printer", originalCassette);
      persistPreference("slide_printer", originalSlide);
    } catch {
      // Preserve the original storage exception for the caller.
    }
    throw error;
  }
}

function buildOptions(
  entries: PrinterCatalogEntry[],
  type: PreferredPrinterType,
  stored: PreferredPrinterPreference | null
) {
  const options = entries
    .filter((entry) => entry.device.deviceType === type)
    .map(({ device, workstation }) => ({
      value: device.id,
      label: `${device.name} — ${workstation.name} — ${workstation.status}, ${device.status}`,
    }));
  if (!stored || options.some((option) => option.value === stored.deviceId)) {
    return options;
  }
  return options.concat({
    value: stored.deviceId,
    label: `${stored.deviceName} — ${stored.workstationName} — status unavailable`,
  });
}

function isStaleSelection(
  type: PreferredPrinterType,
  deviceId: string | null,
  catalog: PrinterCatalog | undefined
): boolean {
  if (workstationPreferenceService.hasInvalidPreferredPrinter(type))
    return true;
  if (!deviceId) return false;
  if (!catalog) return false;
  try {
    resolvePreference(type, deviceId, catalog);
    return false;
  } catch {
    return true;
  }
}

export function usePreferredPrinterSelection(opened: boolean) {
  const [storedCassette, setStoredCassette] =
    useState<PreferredPrinterPreference | null>(() =>
      workstationPreferenceService.getPreferredPrinter("cassette_printer")
    );
  const [storedSlide, setStoredSlide] =
    useState<PreferredPrinterPreference | null>(() =>
      workstationPreferenceService.getPreferredPrinter("slide_printer")
    );
  const [cassettePrinterId, setCassettePrinterId] = useState<string | null>(
    storedCassette?.deviceId ?? null
  );
  const [slidePrinterId, setSlidePrinterId] = useState<string | null>(
    storedSlide?.deviceId ?? null
  );
  useEffect(() => {
    if (!opened) return;
    const cassette =
      workstationPreferenceService.getPreferredPrinter("cassette_printer");
    const slide =
      workstationPreferenceService.getPreferredPrinter("slide_printer");
    setStoredCassette(cassette);
    setStoredSlide(slide);
    setCassettePrinterId(cassette?.deviceId ?? null);
    setSlidePrinterId(slide?.deviceId ?? null);
  }, [opened]);
  const query = useQuery({
    queryKey: ["bridge-preferred-printer-catalog"],
    queryFn: loadPrinterCatalog,
    enabled: opened,
  });
  const entries = useMemo(() => query.data?.entries ?? [], [query.data]);
  const cassettePrinterOptions = useMemo(
    () => buildOptions(entries, "cassette_printer", storedCassette),
    [entries, storedCassette]
  );
  const slidePrinterOptions = useMemo(
    () => buildOptions(entries, "slide_printer", storedSlide),
    [entries, storedSlide]
  );
  const hasStaleCassettePrinter = useMemo(
    () => isStaleSelection("cassette_printer", cassettePrinterId, query.data),
    [cassettePrinterId, query.data]
  );
  const hasStaleSlidePrinter = useMemo(
    () => isStaleSelection("slide_printer", slidePrinterId, query.data),
    [query.data, slidePrinterId]
  );
  const savePreferences = useCallback((): boolean => {
    if (query.isError || !query.data) {
      return false;
    }
    const cassette = resolvePreference(
      "cassette_printer",
      cassettePrinterId,
      query.data
    );
    const slide = resolvePreference(
      "slide_printer",
      slidePrinterId,
      query.data
    );
    persistPreferences(cassette, slide);
    setStoredCassette(cassette);
    setStoredSlide(slide);
    return true;
  }, [cassettePrinterId, query.data, query.isError, slidePrinterId]);

  return {
    cassettePrinterId,
    slidePrinterId,
    cassettePrinterOptions,
    slidePrinterOptions,
    setCassettePrinterId,
    setSlidePrinterId,
    savePreferences,
    isLoading: query.isLoading,
    isError: query.isError,
    hasPartialError: (query.data?.failedWorkstationCount ?? 0) > 0,
    hasStaleCassettePrinter,
    hasStaleSlidePrinter,
  };
}
